/**
 * ProviderMediaItem Component
 * T128-T133: Individual media item display (image/video/caption)
 * 
 * Renders a single media item (photo or video) with caption.
 * Follows the media rendering pattern from resource hub features.
 * 
 * Features:
 * - Image rendering with lazy loading (T131)
 * - Video rendering with HTML5 player (T132)
 * - Caption display below media (T133)
 * - Media type detection (T130)
 * - Aspect ratio containers
 * - Responsive design
 * 
 * Usage:
 * ```tsx
 * <ProviderMediaItem media={mediaItem} />
 * ```
 */

import type { ProviderMedia } from '../types/provider-profile.types';

type ProviderMediaItemProps = {
  media: ProviderMedia;
};

/**
 * Renders a single media item (image or video) with optional caption
 * 
 * @param media - Media item from provider profile
 */
export const ProviderMediaItem = ({ media }: ProviderMediaItemProps) => {
  const { mediaType, mediaUrl, caption } = media;

  return (
    <div className="relative group">
      {/* Media Container with Aspect Ratio */}
      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
        {/* T130, T131: Image Rendering with Lazy Loading */}
        {mediaType === 'image' ? (
          <img
            src={mediaUrl}
            alt={caption || 'Provider photo'}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : /* T130, T132: Video Rendering with HTML5 Player */
        mediaType === 'video' ? (
          <video
            data-testid="media-video"
            src={mediaUrl}
            className="w-full h-full object-cover"
            controls
            preload="metadata"
            controlsList="nodownload"
          >
            <source src={mediaUrl} />
            <p className="text-white p-4 text-sm">
              Your browser doesn't support HTML5 video.
            </p>
          </video>
        ) : null}
      </div>

      {/* T133: Caption Display */}
      {caption && (
        <div className="mt-2 px-1">
          <p className="text-sm text-gray-600 line-clamp-2">{caption}</p>
        </div>
      )}
    </div>
  );
};

